import React, { useEffect } from "react";
import { Box, Button, Modal, Typography, Fade, Backdrop } from "@mui/material";
import type { TenantProfilePayload } from "@/types/tenantService";
import ChipInputField from "@/components/CustomInput/ChipInputField";
import { useTenantProfileUpdate } from "@/hooks/useAdminDashboardService";
import { toast } from "react-toastify";
import type {
  CompanyProfileData,
  UpdateDataType,
} from "@/types/companyProfile";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "#1C1A26",
  border: "2px solid #29273A",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
  color: "#fff",
};

interface EditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; description: string }) => void;
  defaultValues?: { title: string; description: string };
  setInitallData: () => void;
  initallData: TenantProfilePayload;
  updateNeedData: UpdateDataType;
  data: CompanyProfileData;
  refetch: () => void;
  setUpdateNeedData: React.Dispatch<React.SetStateAction<UpdateDataType>>;
}

const EditModal: React.FC<EditModalProps> = ({
  open,
  onClose,
  updateNeedData,
  setUpdateNeedData,
  data,
  refetch,
}) => {
  const {
    about,
    productsServices,
    valueProposition,
    competitors,
    uniqueSellingPoints,
    partners,
    targetIndustries,
    salesMethodology,
  } = data;
  const [initallData, setInitallData] = React.useState<TenantProfilePayload>({
    competitors: [],
    uniqueSellingPoints: [],
    targetIndustries: [],
    partners: [],
    salesMethodology: [],
    reviewText: "",
    whoweare: "",
    valueProposition: [],
    productsServices: [],
  });
  const { mutate } = useTenantProfileUpdate();
  const tenantId = localStorage.getItem("tenantId") || "";
  useEffect(() => {
    if (data) {
      setInitallData((prev) => ({
        ...prev,
        competitors: competitors || [],
        uniqueSellingPoints: uniqueSellingPoints || [],
        targetIndustries: targetIndustries || [],
        partners: partners || [],
        salesMethodology: salesMethodology || [],
        reviewText: data?.reviewText || "",
        whoweare: about || "",
        valueProposition: valueProposition || [],
        productsServices: productsServices || [],
      }));
    }
  }, [data]);

  const handleSave = () => {
    handleSubmit();
  };
  const handleSubmit = () => {
    const payload: TenantProfilePayload = {
      competitors: initallData.competitors,
      uniqueSellingPoints: initallData.uniqueSellingPoints,
      targetIndustries: initallData.targetIndustries,
      partners: initallData.partners,
      salesMethodology: initallData.salesMethodology,
      reviewText: initallData.reviewText,
      whoweare: initallData.whoweare,
      valueProposition: initallData.valueProposition,
      productsServices: initallData.productsServices,
    };

    mutate(
      {
        id: tenantId,
        payload,
      },
      {
        onSuccess: () => {
          toast.success("Profile Update Successfully...");
          refetch();
          onClose();
        },
        onError: (error: Error) => {
          console.error("Profile Update Failed:", error.message);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Typography variant="h6" mb={2}>
            Edit Section
          </Typography>

          <ChipInputField
            label={updateNeedData.title}
            values={updateNeedData.value}
            onChange={(newValues) => {
              setUpdateNeedData((prev) => ({
                ...prev,
                value: newValues,
              }));
              setInitallData((prev) => ({
                ...prev,
                [updateNeedData.titleMain]: newValues,
              }));
            }}
          />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ borderColor: "#B8F890", color: "#B8F890" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                bgcolor: "#B8F890",
                color: "#000",
                "&:hover": { bgcolor: "#A4E67E" },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default EditModal;
